import _ from 'lodash'
import { withDependencies, named } from '@wix/thunderbolt-ioc'
import { EditorFeatureConfigSymbol, ILogger, LoggerSymbol } from '@wix/thunderbolt-symbols'
import { IHoverBoxAPI, HoverBoxAPISym, Modes } from 'feature-hover-box'
import type { IViewerManagerImplementor } from 'thunderbolt-viewer-manager-types'
import type { ComponentWillMount, ViewerComponent } from 'feature-components'
import type { ViewerManagerModesApiEditorConfig } from './types'
import { name, ModesApiSymbol } from './symbols'

type ReactivateIfNeeded = { reactivateIfNeeded: (id: string) => void }

export const ViewerManagerModesApi = withDependencies(
	[named(EditorFeatureConfigSymbol, name), HoverBoxAPISym, LoggerSymbol],
	(
		editorConfig: ViewerManagerModesApiEditorConfig,
		hoverBoxApi: IHoverBoxAPI,
		logger: ILogger
	): IViewerManagerImplementor<'modes'> & ReactivateIfNeeded => {
		const activeModesOverrides: Record<string, boolean> = {}
		const activateMode = (defaultCompId: string, modeId: string) => {
			// masterPage component contains sosp mode data - currently we are not support sosp mode for this api
			if (defaultCompId === 'masterPage') {
				return
			}

			const compModes = editorConfig.compIdToModes[defaultCompId]
			if (!compModes) {
				logger.captureError(new Error('component does not have modes'), {
					tags: { feature: 'modes', methodName: 'activateMode' },
					extra: { compId: defaultCompId },
				})
				return
			}

			hoverBoxApi.updateHoverBoxesMode(
				defaultCompId,
				`${defaultCompId}:hover`,
				editorConfig.hoverBoxParents,
				compModes[modeId].toLowerCase() as Modes
			)
			activeModesOverrides[modeId] = true
			const otherModeId = Object.keys(compModes).find((x) => x !== modeId)
			activeModesOverrides[otherModeId!] = false
		}

		const deactivateMode = (defaultCompId: string, modeId: string) => {
			const otherModeId = Object.keys(editorConfig.compIdToModes[defaultCompId]).find((x) => x !== modeId)
			activateMode(defaultCompId, otherModeId!)
		}

		const getAllActiveModeIds = () => {
			const allActiveModes = { ...editorConfig.activeModes, ...activeModesOverrides }
			return _.omitBy(allActiveModes, (isActive) => !isActive)
		}

		return {
			reactivateIfNeeded: (id) => {
				_.forEach(editorConfig.compIdToModes[id], (modeType, modeId) => {
					if (modeType === 'HOVER' && activeModesOverrides[modeId]) {
						activateMode(id, modeId)
					}
				})
			},
			modes: {
				activateMode,
				deactivateMode,
				switchModes: () => {
					// TODO	remove this API when viewerAPIInterface will be updated
				},
				getAllActiveModeIds,
				getAllActiveModes: () => {
					const activeModeIds = getAllActiveModeIds()
					return _.mapValues(editorConfig.pageIdToModes, (modes) =>
						_(activeModeIds)
							.omitBy((val, modeId) => !modes[modeId])
							.mapValues((val, modeId) => ({ modeType: modes[modeId] }))
							.value()
					)
				},
				resetAllActiveModes: () => {
					Object.keys(getAllActiveModeIds()).forEach((modeId) => {
						const { compIdToModes, modeIdToCompId } = editorConfig
						const compId = modeIdToCompId[modeId]
						if (compId && compIdToModes[compId][modeId] === 'HOVER') {
							deactivateMode(compId, modeId)
						}
					})
				},
				// currently we are not support sosp for this api
				updateActiveSOSPModes: () => {},
			},
		}
	}
)

export const ModesComponentWillMount = withDependencies(
	[ModesApiSymbol],
	(modesAPI: ReactivateIfNeeded): ComponentWillMount<ViewerComponent> => ({
		componentTypes: ['HoverBox'],
		componentWillMount: ({ id }) => {
			modesAPI.reactivateIfNeeded(id)
		},
	})
)
